import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useDeleteGameMutation } from './hooks/useDeleteGameMutation';
import { GamifyToast } from 'application/common/CustomToasts';
import ConfirmationModal from 'application/routes/users/ConfirmationModal';
import GameCardMenu from 'application/components/GameCardMenu/GameCardMenu';
import Backdrop from '@mui/material/Backdrop';
import DisplayMedia from '../../components/DisplayMedia';
import { AddIcon, GamifyIcon } from 'application/components/GamifyIcon';
import NewGameContainer from './NewGame/NewGame.container';
import Game from './_types/Game';
import { GameCarouselType, ScopeType, useGetGames } from './hooks/useGetGames';

import {
  ViewContainer,
  HeaderContainer,
  HeaderLeft,
  HeaderTitle,
  HeaderStats,
  StatItem,
  HeaderRight,
  MainContent,
  TabsContainer,
  Tab,
  GameCardsContainer,
  GameCard,
  GameCardImage,
  GameCardContent,
  GameCardEllipsis,
  GameCardTitle,
  SkeletonContainer,
  SkeletonCardsContainer,
  SkeletonCard,
  EmptyStateMessage,
  GameSectionTitle,
  NewGameButton,
} from './Games.styles';

interface Props {
  showAdd: boolean;
}

const TABS = [
  { id: ScopeType.all, label: 'All' },
  { id: ScopeType.person, label: 'Individual' },
  { id: ScopeType.circle, label: 'Teams' },
];

const renderSkeletonSection = () => (
  <SkeletonContainer>
    <SkeletonCardsContainer>
      {[1, 2, 3].map((key) => (
        <SkeletonCard key={key}>
          <div className="image" />
          <div className="title" />
          <div className="subtitle" />
        </SkeletonCard>
      ))}
    </SkeletonCardsContainer>
  </SkeletonContainer>
);

const GamesView = ({ showAdd }: Props) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ScopeType>(ScopeType.all);
  const [showNewGameModal, setShowNewGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState<Game | null>(null);

  // Move menu-related state to parent component
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteGameMutation = useDeleteGameMutation();

  const { data: currentGames, isLoading: isLoadingCurrent } = useGetGames(
    GameCarouselType.current,
    activeTab
  );

  const { data: futureGames, isLoading: isLoadingFuture } = useGetGames(
    GameCarouselType.future,
    activeTab
  );

  const { data: pastGames, isLoading: isLoadingPast } = useGetGames(
    GameCarouselType.past,
    activeTab
  );

  const totalGames =
    (currentGames?.length || 0) +
    (futureGames?.length || 0) +
    (pastGames?.length || 0);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    game: Game
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedGame(game);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditGame = (game: Game) => {
    setEditingGame(game);
    setShowNewGameModal(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setShowDeleteConfirmation(false);
    setSelectedGame(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedGame) return;

    try {
      setIsDeleting(true);
      await deleteGameMutation.mutateAsync({ missionID: selectedGame.id });
      GamifyToast.success('Success', 'Game has been deleted successfully');
    } catch (error) {
      console.error('Failed to delete game:', error);
      GamifyToast.error('Error', 'Failed to delete game');
    } finally {
      setIsDeleting(false);
      handleCloseModal();
    }
  };

  const handleNewGameModalClose = () => {
    // The state reset will be handled in the NewGameContainer's handleClose function
    setShowNewGameModal(false);
    setEditingGame(null);
  };

  const renderGameSection = (
    title: string,
    games: Game[] | undefined,
    isLoading: boolean
  ) => {
    return (
      <>
        <GameSectionTitle>{title}</GameSectionTitle>
        {isLoading ? (
          renderSkeletonSection()
        ) : !games?.length ? (
          <EmptyStateMessage>
            There are no games for this category
          </EmptyStateMessage>
        ) : (
          <GameCardsContainer>
            {games.map((game) => (
              <GameCard
                key={game.id}
                onClick={() => navigate(`/performance/games/${game.id}`)}
              >
                <GameCardImage>
                  <GameCardEllipsis onClick={(e) => handleMenuClick(e, game)}>
                    <GamifyIcon
                      icon="ellipsis"
                      color="#323232"
                      height={10}
                      width={10}
                    />
                  </GameCardEllipsis>
                  <DisplayMedia
                    imageName={
                      game.imageName || '/images/trophy-placeholder.jpeg'
                    }
                    imageType={game.imageName ? 'picture' : 'giftcard_url'}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    noWrapper
                    sx={{ borderRadius: '8px 8px 0 0' }}
                  />
                </GameCardImage>
                <GameCardContent>
                  <GameCardTitle>{game.name}</GameCardTitle>
                </GameCardContent>
              </GameCard>
            ))}
          </GameCardsContainer>
        )}
      </>
    );
  };

  return (
    <ViewContainer>
      <HeaderContainer>
        <HeaderLeft>
          <HeaderTitle>Games</HeaderTitle>
          <HeaderStats>
            <StatItem>
              <GamifyIcon icon="scroll" />
              {totalGames}
            </StatItem>
          </HeaderStats>
        </HeaderLeft>
        <HeaderRight>
          {showAdd && (
            <NewGameButton onClick={() => setShowNewGameModal(true)}>
              <AddIcon /> New game
            </NewGameButton>
          )}
        </HeaderRight>
      </HeaderContainer>

      <Backdrop
        sx={{
          zIndex: 1000,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        open={showNewGameModal}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: 'auto',
          }}
        >
          <NewGameContainer
            key={`game-modal-${editingGame?.id || 'new'}-${showNewGameModal}`}
            onClose={handleNewGameModalClose}
            gameId={editingGame?.id}
            onSaveComplete={handleNewGameModalClose}
          />
        </div>
      </Backdrop>

      <TabsContainer>
        {TABS.map((tab) => (
          <Tab
            key={tab.id}
            isActive={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Tab>
        ))}
      </TabsContainer>

      <MainContent>
        {renderGameSection('Current Games', currentGames, isLoadingCurrent)}
        {renderGameSection('Future Games', futureGames, isLoadingFuture)}
        {renderGameSection('Past Games', pastGames, isLoadingPast)}
      </MainContent>

      {/* Move menu and confirmation modal outside of sections */}
      <GameCardMenu
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        onEdit={() => selectedGame && handleEditGame(selectedGame)}
        onDelete={handleDelete}
      />

      <ConfirmationModal
        open={showDeleteConfirmation}
        onClose={handleCloseModal}
        title="Delete Game"
        message={
          selectedGame
            ? `Are you sure you want to delete "${selectedGame.name}"? This action cannot be undone.`
            : ''
        }
        confirmationButtonText="Delete"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </ViewContainer>
  );
};

export default GamesView;
